import React, { useState, useEffect, useMemo } from "react";
import { clientsApi } from "../../../services";
import type { Car, EffortLevel } from "../../../types";
import type { CarFormData } from "../../../services/clientsApi";
import { getCarBrandSelectOptions } from "../../../constants/carBrands";
import { Modal, ErrorState, Input, FormActions, Select } from "../../shared";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { MercosulPlate } from "./MercosulPlate";

interface CreateCarModalProps {
  isOpen: boolean;
  onClose: () => void;
  car?: Car | null;
  clientId: number;
  clientName: string;
}

const BRAND_OPTIONS = getCarBrandSelectOptions();

export const CreateCarModal: React.FC<CreateCarModalProps> = ({
  isOpen,
  onClose,
  car,
  clientId,
  clientName,
}) => {
  const [formData, setFormData] = useState<CarFormData>({
    clientId,
    model: car?.model || "",
    plate: car?.plate || "",
    brand: car?.brand || "",
    effort: car?.effort || undefined,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = Boolean(car);

  const brandSelectOptions = useMemo(
    () => [{ value: "", label: "Sem marca" }, ...BRAND_OPTIONS],
    []
  );

  useEffect(() => {
    if (car) {
      setFormData({
        clientId,
        model: car.model,
        plate: car.plate,
        brand: car.brand || "",
        effort: car.effort,
      });
    } else {
      setFormData({
        clientId,
        model: "",
        plate: "",
        brand: "",
        effort: undefined,
      });
    }
    setError(null);
  }, [car, clientId]);

  const handleInputChange = (
    field: keyof CarFormData,
    value: string | EffortLevel | undefined | null
  ) => {
    setFormData((prev: CarFormData) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  const handlePlateChange = (value: string) => {
    handleInputChange("plate", value);
  };

  const validateForm = (): boolean => {
    if (!formData.model.trim()) {
      setError("Modelo é obrigatório");
      return false;
    }

    if (!formData.plate.trim()) {
      setError("Placa é obrigatória");
      return false;
    }

    if (formData.plate.length < 8) {
      setError("Placa deve ter pelo menos 7 caracteres");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    const payload: CarFormData = {
      ...formData,
      brand: formData.brand?.trim() || null,
    };

    try {
      const response =
        isEditing && car
          ? await clientsApi.updateCar(car.id, payload)
          : await clientsApi.createCar(payload);

      if (response.success) {
        onClose();
      } else {
        setError(
          response.error || `Failed to ${isEditing ? "update" : "create"} car`
        );
      }
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "creating"} car:`, error);
      setError(`Failed to ${isEditing ? "update" : "create"} car`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Editar Veículo" : "Novo Veículo"}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {clientName?.charAt(0)?.toUpperCase() || "?"}
              </span>
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-900">Cliente</h4>
              <p className="text-blue-700">{clientName || "Cliente"}</p>
            </div>
          </div>
        </div>

        {error && <ErrorState message={error} variant="inline" size="sm" />}

        <div className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Select
                label="Marca"
                value={formData.brand || ""}
                onChange={(value) =>
                  handleInputChange("brand", value === "" ? null : value)
                }
                options={brandSelectOptions}
                placeholder="Selecione a marca (opcional)"
                searchable
              />
            </div>
            <CarBrandLogo brand={formData.brand} />
          </div>

          <Input
            label="Modelo do Veículo"
            type="text"
            value={formData.model}
            onChange={(e) => handleInputChange("model", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as React.FormEvent);
              }
            }}
            placeholder="Ex: Civic, Gol, HB20..."
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Placa *
            </label>
            <div className="flex justify-center">
              <MercosulPlate
                value={formData.plate}
                onChange={(value) => handlePlateChange(value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e as React.FormEvent);
                  }
                }}
                placeholder="ABC-1D23"
              />
            </div>
          </div>

          <Select
            label="Esforço"
            value={formData.effort || ""}
            onChange={(value) =>
              handleInputChange(
                "effort",
                value === "" ? undefined : (value as EffortLevel)
              )
            }
            options={[
              { value: "Leve", label: "Leve" },
              { value: "Médio", label: "Médio" },
              { value: "Pesado", label: "Pesado" },
            ]}
            placeholder="Selecione o esforço (opcional)"
            searchable={false}
          />
        </div>

        <FormActions
          onCancel={onClose}
          onSubmit={() => {}}
          loading={loading}
          isEditing={isEditing}
          submitText={isEditing ? "Salvar Alterações" : "Criar Veículo"}
          submitType="submit"
        />
      </form>
    </Modal>
  );
};
